/**
 * Форми слова для повідомлень платформи (metadata-ui.md, розд. 2; AG-04, UX-42). Окремий шлях
 * `@clientline/contracts/i18n` без zod: помічник потрібен першому екрану (AG-61).
 * Платформа не знає самого слова — лише форми з метаданих сутності.
 */
export interface NounUk {
  nom: readonly [string, string];
  gen: readonly [string, string];
  acc: readonly [string, string];
  gender: 'm' | 'f' | 'n';
}

export type NounCase = 'nom' | 'gen' | 'acc';

/** Категорія CLDR для української: one · few · many · other. */
export function pluralUk(count: number): 'one' | 'few' | 'many' | 'other' {
  if (!Number.isInteger(count)) return 'other';
  const n = Math.abs(count);
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'one';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';
  return 'many';
}

/**
 * Форма іменника. Без `count` — відмінок в однині чи множині (`plural`). З `count` — за таблицею
 * розд. 2: після числа (`nom`) — one → nom[0], few → nom[1], many → gen[1], other → gen[0];
 * після дієслова (`acc`) — one → acc[0], few → acc[1], many → gen[1], other → gen[0].
 */
export function noun(
  label: NounUk,
  options: { case?: NounCase; count?: number; plural?: boolean } = {},
): string {
  const form = options.case ?? 'nom';
  if (options.count === undefined) return label[form][options.plural ? 1 : 0];
  const category = pluralUk(options.count);
  if (category === 'many') return label.gen[1];
  if (category === 'other') return label.gen[0];
  const after = form === 'acc' ? label.acc : label.nom;
  return after[category === 'one' ? 0 : 1];
}

/** Перша літера велика: форми в метаданих пишуться з малої (metadata-ui.md, розд. 2). */
export function capitalize(text: string): string {
  return text.length === 0 ? text : text[0]?.toLocaleUpperCase('uk') + text.slice(1);
}
